import classname from 'classnames';
import * as React from 'react';
import { getNZAddressResponseAction, GetNZPostAddressResponseActionInput } from '../../data-actions/address/get-nz-post-address';
import { getNZAddressResponseDetailsAction, GetNZPostAddressDetailsActionInput } from '../../data-actions/address/get-nz-post-address-details';
import { IAddressDetails, IDXC_Address } from '../../data-actions/generated/DataServiceEntities.g';

interface IState {
    showAddressList: boolean;
    searchAddress: string | undefined;
    selectedAddress: IAddressDetails | undefined;
    formattedAddress: IFormattedAddressDetails | undefined;
    addressList: IDXC_Address[] | undefined;
    messageID: string | undefined;
    manualAddressLabel: string;
    searchAddressLabel: string;
    errorMessage: string | undefined;
}

interface IProp {
    // tslint:disable-next-line: no-any
    context: any; // needed for API calls
    manualAddress: boolean; // boolean toggle for manual address input - passed from wrapper module
    addressSelectedCallback(address: IFormattedAddressDetails | undefined): void; // passes selected address to wrapper module
    toggleManualAddressCallback(): void; // called to toggle search and manual address input (should toggle manualAddress variable in wrapper module)
}

/**
 * AddressSearch component
 */
class AddressSearch extends React.Component<IProp, IState> {
    private _SearchAddressInputRef: React.RefObject<HTMLInputElement>;

    constructor(props: IProp) {
        super(props);
        this._SearchAddressInputRef = React.createRef();

        this.state = {
            showAddressList: false,
            searchAddress: '',
            selectedAddress: undefined,
            formattedAddress: undefined,
            addressList: [],
            messageID: '',
            manualAddressLabel: 'Enter Address Manually',
            searchAddressLabel: 'Search for your Address',
            errorMessage: undefined
        };
    }

    public render(): JSX.Element {
        return(
            <div>
                {!this.props.manualAddress &&
                    <div className='address-search search-input'>
                        <div className='address-search-heading'>Search Address</div>
                        <div className='inputSearchIcon'>
                            <div className='c-defInput'>
                                <input
                                    ref={this._SearchAddressInputRef}
                                    type='text'
                                    onChange={this.getNZAddress()}
                                    aria-label='Search Address'
                                    aria-autoComplete='both'
                                    className={classname('search-address-input-text', this.state.showAddressList ? 'showingAddress' : '')}
                                    placeholder='Search NZ address...'
                                    value={this.state.searchAddress}
                                />
                            </div>
                        </div>
                        {this.state.showAddressList && this.state.addressList &&
                            <div className='address-list'>
                                <ul className='address-list-ul'>
                                    {this.state.addressList.map((address: IDXC_Address, index: number) => {
                                        if (address) {
                                            const addressId = address && address.address_id ? address.address_id : '';
                                            const fullAddress = address && address.full_address ? address.full_address : '';
                                            return (
                                                <li className='address-list-li' key={index}>
                                                    <a className='address-text' role='link' onClick={this.getNZAddressDetails(addressId, fullAddress)}>{address.full_address}</a>
                                                </li>
                                            );
                                        }
                                        return null;
                                    })}
                                </ul>
                            </div>
                        }
                    </div>
                }
                {this.state.errorMessage &&
                    <div className='searchAddressError'>
                        <span>{this.state.errorMessage}</span>
                    </div>
                }
                <div className='searchAddressNote'>
                    <a className='triggerManualAddress' role='link' onClick={this.toggleManualAddress()}>{this.props.manualAddress ? this.state.searchAddressLabel : this.state.manualAddressLabel}</a>
                </div>
            </div>
        );
    }

    public clearAddress = () => {
        this.setState({
            searchAddress: '',
            selectedAddress: undefined,
            formattedAddress: undefined
        });
    }

    /**
     * Calls lookahead search API for currently typed input string
     */
    private getNZAddress = () => (e: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement> | React.ChangeEvent<HTMLInputElement>) => {
        const inputText = this._SearchAddressInputRef.current && this._SearchAddressInputRef.current.value || '';
        this.setState({
            searchAddress: inputText,
            selectedAddress: undefined,
            formattedAddress: undefined,
            errorMessage: undefined
        });
        this.props.addressSelectedCallback(undefined);
        if (inputText.length > 3) {
            getNZAddressResponseAction(new GetNZPostAddressResponseActionInput(inputText), this.props.context.actionContext).then(
                (result) => {

                    this.setState({
                        addressList: result.addresses,
                        messageID: result.message_id,
                        showAddressList: true
                    });
                }
            )
            .catch(() => {
                console.log('No data');
            });
        }
    }

    /**
     * Calls API to get full details of selected address
     */
    private getNZAddressDetails = (addressId: string, fullAddress: string) => (e: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement> | React.ChangeEvent<HTMLInputElement>) => {
        getNZAddressResponseDetailsAction(new GetNZPostAddressDetailsActionInput(addressId), this.props.context.actionContext).then(
            (result) => {
                console.log('result', result);
                const formattedAddressObject = this._getFormattedAddressObject(result.address);
                this.setState({
                    showAddressList: false,
                    searchAddress: result.address ? fullAddress : this.state.searchAddress,
                    selectedAddress: result.address,
                    formattedAddress: formattedAddressObject,
                    errorMessage: result.address ? undefined : 'There was an error retrieving this address, if this error persists please enter your address manually'
                });
                this.props.addressSelectedCallback(formattedAddressObject);
            }
        )
        .catch(() => {
            console.log('No data');
        });
    }

    /**
     * Formats the address object to match data needed in AX
     * @param address Address object from NZ Post API
     */
    private _getFormattedAddressObject(address: IAddressDetails | undefined): IFormattedAddressDetails | undefined {
        if (!address) {
            return;
        }
        console.log('address', address)
        return {
            building_name: this._getFormattedBuildingName(address),
            street_number: this._getFormattedStreetNumber(address),
            street: this._getFormattedStreet(address),
            suburb: address.suburb,
            city: address.city,
            postcode: address.postcode,
            country: address.country,
        };
    }

    private _getFormattedBuildingName(address: IAddressDetails): string | undefined {
        if (!(address.unit_type || address.unit_value || address.floor)) {
            return;
        }

        let buildingName = address.unit_type ? address.unit_type : '';
        buildingName += address.unit_value ? ` ${address.unit_value}` : '';
        if (address.floor) {
            buildingName.length > 0 ? buildingName += ` ${address.floor}` : buildingName = address.floor;
        }
        return buildingName;
    }

    private _getFormattedStreetNumber(address: IAddressDetails): string | undefined {
        if (!(address.street_number || address.street_alpha)) {
            return;
        }
        let streetNumDetails = address.street_number ? `${address.street_number}` : '';
        streetNumDetails += address.street_alpha ? address.street_alpha : '';
        return streetNumDetails;
    }

    private _getFormattedStreet(address: IAddressDetails): string | undefined {
        if (!(address.street || address.street_type || address.road_suffix_name)) {
            return;
        }
        let street = address.street ? address.street : '';
        street += address.street_type ? ` ${address.street_type}` : '';
        street += address.road_suffix_name ? ` ${address.road_suffix_name}` : '';

        if (address.rural_delivery_number) {
            street += `, ${address.rural_delivery_number}` 
        }
        return street;
    }

    /**
     * onClick function to trigger manual input toggle callback
     */
    private toggleManualAddress = () => (e: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement> | React.ChangeEvent<HTMLInputElement>) => {
        this.props.toggleManualAddressCallback();
        this.clearAddress();
    }
}

export default AddressSearch;

/**
 * Formatted address object
 */
export interface IFormattedAddressDetails {
    building_name?: string;

    street_number?: string;

    street?: string;

    suburb?: string;

    city?: string;

    postcode?: string;

    country?: string;

    rural_delivery_number?: string;
}
